import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import {
  get as rtdbGet,
  off as rtdbOff,
  onValue,
  ref as rtdbRef,
  remove as rtdbRemove,
} from "firebase/database";
import AdminLayout from "../../components/layouts/AdminLayout";
import { database, firestore } from "../../firebaseConfig";
import { Calendar, Eye, Loader2, Search, Wallet, X } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { buildDateRange, clampDateToToday } from "./b2cDateHelpers";
import { getStorage, ref as storageRef, uploadBytes } from "firebase/storage";

const currencyLabel = "KES";
const MAX_DATE_HIERARCHY_LIMIT =
  Number(process.env.REACT_APP_B2C_DATE_LIMIT) || 400;
const MAX_PHONE_LOOKUP_LIMIT =
  Number(process.env.REACT_APP_B2C_PHONE_LIMIT) || 200;
const MAX_DATE_RANGE_DAYS =
  Number(process.env.REACT_APP_B2C_DATE_RANGE_DAYS) || 31;

// TODO: Replace with your real base64 data URL for the logo (PNG/JPEG).
// Example: const LOGO_DATA_URL = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...";
const LOGO_DATA_URL = "https://firebasestorage.googleapis.com/v0/b/neuracraft-378a2.firebasestorage.app/o/Logo%2Fnc_logo_2.png?alt=media&token=6a70a5af-313a-4516-be1c-31f0ddbf13fd";

const defaultPhoneModalState = {
  open: false,
  phone: "",
  loading: false,
  records: [],
  error: null,
};

const RECEIPT_COLORS = {
  primary: [245, 124, 0],
  dark: [230, 81, 0],
  light: [255, 243, 224],
  watermark: [255, 224, 178],
  headerGrey: [66, 66, 66],
  border: [218, 218, 218],
};

const defaultReceiptModalState = {
  open: false,
  purchaseAmount: "",
  totalAmount: "",
  transactionCost: "",
  balance: "",
  advance: "",
  submitting: false,
  error: "",
};

const StatusBadge = ({ status }) => {
  const normalized = (status || "pending").toString().toLowerCase();
  const color =
    normalized === "completed"
      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
      : normalized === "failed"
      ? "bg-red-50 text-red-700 border-red-100"
      : "bg-slate-50 text-slate-700 border-slate-200";
  const dot =
    normalized === "completed"
      ? "bg-emerald-500"
      : normalized === "failed"
      ? "bg-red-500"
      : "bg-slate-400";
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-semibold border capitalize inline-flex items-center gap-2 ${color}`}
    >
      <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
      {normalized}
    </span>
  );
};

const formatDate = (value) => {
  if (!value) return "—";
  if (value.toDate) return value.toDate().toLocaleString();
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const getTransactionTime = (tx) => {
  const value = tx?.transactionDate || tx?.processedAt;
  const date = value?.toDate ? value.toDate() : new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
};

const normalizePhone = (value) => {
  if (value === null || value === undefined) return "";
  const raw = String(value).trim();
  let numericLike = null;
  if (/e\+/i.test(raw)) {
    numericLike = Number.parseFloat(raw);
  } else if (Number.isFinite(Number(raw))) {
    numericLike = Number(raw);
  }
  const candidate =
    numericLike !== null && !Number.isNaN(numericLike)
      ? Math.trunc(numericLike).toString()
      : raw;
  const digits = candidate.replace(/\D/g, "");
  if (digits.startsWith("0") && digits.length === 10)
    return `254${digits.slice(1)}`;
  if ((digits.startsWith("7") || digits.startsWith("1")) && digits.length === 9)
    return `254${digits}`;
  if (digits.startsWith("254")) return digits.slice(0, 12);
  return digits;
};

const extractTxPhone = (tx) =>
  tx?.receiverPhone ||
  tx?.phoneNumber ||
  tx?.MSISDN ||
  tx?.receiverMSISDN ||
  tx?.payerPhone ||
  tx?.phone ||
  "";

const extractProposedPhone = (payment) =>
  payment?.PhoneNumber || payment?.phoneNumber || payment?.MSISDN || "";

const getValidatedPhoneFallback = (payment, farmerMap) => {
  if (!payment || !farmerMap) return "";
  const key =
    payment.OtherName || payment.otherName || payment.CloudName || payment._key;
  if (!key || !farmerMap[key]) return "";
  const farmer = farmerMap[key] || {};
  return (
    farmer.PhoneNumber ||
    farmer.MSISDN ||
    farmer.phoneNumber ||
    farmer.phone ||
    ""
  );
};

const getResolvedProposedPhone = (payment, farmerMap) =>
  extractProposedPhone(payment) || getValidatedPhoneFallback(payment, farmerMap);

const getComparableAmount = (value) =>
  Number(Number(value || 0).toFixed(2)) || 0;

const getPaymentMatchKey = (phone, amount) =>
  `${normalizePhone(phone)}|${getComparableAmount(amount)}`;

const getProposedPaymentKey = (payment) =>
  `${payment._key}|${payment.TransactionDate || payment.__date || ""}`;

const getProposedDisplayName = (payment) =>
  payment.CloudName || payment.OtherName || "Unknown";

const AdminB2CTransactions = () => {
  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(true);

  const todayIso = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(todayIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [proposedStartDate, setProposedStartDate] = useState(todayIso);
  const [proposedEndDate, setProposedEndDate] = useState(todayIso);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [proposedRows, setProposedRows] = useState([]);
  const [loadingProposedRows, setLoadingProposedRows] = useState(false);
  const [completedPageSize, setCompletedPageSize] = useState(20);
  const [proposedPageSize, setProposedPageSize] = useState(20);
  const [completedPage, setCompletedPage] = useState(1);
  const [proposedPage, setProposedPage] = useState(1);
  const [validatedFarmers, setValidatedFarmers] = useState({});

  const [pendingPage, setPendingPage] = useState(1);
  const PENDING_PAGE_SIZE = 20;

  const [pendingRtdbRows, setPendingRtdbRows] = useState([]);
  const [pendingRtdbLoading, setPendingRtdbLoading] = useState(false);
  const [clearingPending, setClearingPending] = useState(false);

  const [phoneModal, setPhoneModal] = useState(defaultPhoneModalState);
  const [receiptModal, setReceiptModal] = useState(defaultReceiptModalState);

  const clampDate = (value) => clampDateToToday(value, todayIso);
  const handleDateChange = (setter) => (value) => setter(clampDate(value));

  useEffect(() => {
    const loadCompanies = async () => {
      if (!firestore) {
        setLoadingCompanies(false);
        return;
      }
      try {
        const snap = await getDocs(
          query(
            collection(firestore, "companies"),
            orderBy("companyName", "asc")
          )
        );
        const companyRows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setCompanies(companyRows);
        setSelectedCompanyId((prev) => prev || companyRows[0]?.id || "");
      } catch (err) {
        console.error("Failed to load companies", err);
      } finally {
        setLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, []);

  useEffect(() => {
    const loadForDateRange = async () => {
      if (!firestore) {
        setLoadingRows(false);
        return;
      }

      const requestedDates = buildDateRange(
        startDate,
        endDate,
        MAX_DATE_RANGE_DAYS
      );
      if (!requestedDates.length) {
        setRows([]);
        setLoadingRows(false);
        return;
      }

      setLoadingRows(true);
      try {
        const result = [];
        await Promise.all(
          requestedDates.map(async (isoDate) => {
            const [year, month, day] = isoDate.split("-");
            try {
              const colRef = collection(
                firestore,
                "b2c_transactions_by_date",
                year,
                month,
                day,
                "tx"
              );
              const q = query(
                colRef,
                orderBy("processedAt", "desc"),
                limit(MAX_DATE_HIERARCHY_LIMIT)
              );
              const snap = await getDocs(q);
              snap.forEach((docSnap) => {
                result.push({
                  id: docSnap.id,
                  ...docSnap.data(),
                  __path: docSnap.ref.path,
                });
              });
            } catch (err) {
              console.error(
                "Failed to load B2C transactions for date",
                isoDate,
                err
              );
            }
          })
        );

        result.sort((a, b) => getTransactionTime(b) - getTransactionTime(a));
        setRows(result);
      } catch (err) {
        console.error("Failed to load B2C transactions for date range", err);
        setRows([]);
      } finally {
        setLoadingRows(false);
      }
    };

    loadForDateRange();
  }, [startDate, endDate]);

  useEffect(() => {
    const loadProposedRows = async () => {
      if (!database || !selectedCompanyId) {
        setProposedRows([]);
        setLoadingProposedRows(false);
        return;
      }
      const requestedDates = buildDateRange(
        proposedStartDate,
        proposedEndDate,
        MAX_DATE_RANGE_DAYS
      );
      if (!requestedDates.length) {
        setProposedRows([]);
        setLoadingProposedRows(false);
        return;
      }

      setLoadingProposedRows(true);
      try {
        const proposed = [];
        await Promise.all(
          requestedDates.map(async (isoDate) => {
            const dateObj = new Date(isoDate);
            const year = String(dateObj.getFullYear());
            const monthName = dateObj.toLocaleString("en-US", { month: "long" });
            const paddedDay = String(dateObj.getDate()).padStart(2, "0");
            const rawDay = String(dateObj.getDate());
            const dayKeys =
              paddedDay === rawDay ? [paddedDay] : [paddedDay, rawDay];

            await Promise.all(
              dayKeys.map(async (dayKey) => {
                const nodeRef = rtdbRef(
                  database,
                  `Companies/${selectedCompanyId}/Payments/${year}/${monthName}/${dayKey}`
                );
                const snap = await rtdbGet(nodeRef);
                if (!snap.exists()) return;
                const value = snap.val() || {};
                Object.entries(value).forEach(([key, payment]) => {
                  proposed.push({
                    _key: key,
                    ...payment,
                    __date: isoDate,
                  });
                });
              })
            );
          })
        );
        const uniqueProposed = Array.from(
          new Map(
            proposed.map((payment) => [getProposedPaymentKey(payment), payment])
          ).values()
        );
        uniqueProposed.sort((a, b) =>
          getProposedDisplayName(a).localeCompare(getProposedDisplayName(b))
        );
        setProposedRows(uniqueProposed);
      } catch (err) {
        console.error("Failed to load proposed company payments", err);
        setProposedRows([]);
      } finally {
        setLoadingProposedRows(false);
      }
    };

    loadProposedRows();
  }, [proposedStartDate, proposedEndDate, selectedCompanyId]);

  useEffect(() => {
    const loadValidatedFarmers = async () => {
      if (!database || !selectedCompanyId) {
        setValidatedFarmers({});
        return;
      }
      try {
        const snap = await rtdbGet(
          rtdbRef(database, `Companies/${selectedCompanyId}/ValidatedFarmers`)
        );
        setValidatedFarmers(snap.exists() ? snap.val() || {} : {});
      } catch (err) {
        console.error("Failed to load validated farmers", err);
        setValidatedFarmers({});
      }
    };
    loadValidatedFarmers();
  }, [database, selectedCompanyId]);

  useEffect(() => {
    if (!database || !selectedCompanyId) {
      setPendingRtdbRows([]);
      setPendingRtdbLoading(false);
      return;
    }

    setPendingRtdbLoading(true);
    const path = `Companies/${selectedCompanyId}/NewPaymentRecord`;
    const nodeRef = rtdbRef(database, path);

    const handler = (snap) => {
      const val = snap.val() || {};
      const arr = Object.entries(val)
        .map(([key, payment]) => ({ ...payment, _key: key }))
        .sort((a, b) =>
          (b?.TransactionDate || "").localeCompare(a?.TransactionDate || "")
        );
      setPendingRtdbRows(arr);
      setPendingRtdbLoading(false);
    };

    onValue(nodeRef, handler, (err) => {
      console.error("NewPaymentRecord listener error", err);
      setPendingRtdbLoading(false);
    });

    return () => {
      rtdbOff(nodeRef, "value", handler);
    };
  }, [selectedCompanyId]);

  const setPhoneModalState = (overrides) =>
    setPhoneModal({ ...defaultPhoneModalState, ...overrides });
  const closePhoneModal = () => setPhoneModal(defaultPhoneModalState);

  const setReceiptModalState = (overrides) =>
    setReceiptModal((prev) => ({ ...prev, ...overrides }));

  const resetReceiptModal = () => setReceiptModal(defaultReceiptModalState);

  const openPhoneModalForTx = async (tx) => {
    const phoneRaw = extractTxPhone(tx);
    const phone = normalizePhone(phoneRaw);

    setPhoneModalState({
      open: true,
      phone: phone || phoneRaw || "",
      loading: true,
    });

    if (!firestore) {
      setPhoneModalState({
        open: true,
        phone: phone || phoneRaw || "",
        error: "Firestore is not configured.",
      });
      return;
    }
    if (!phone) {
      setPhoneModalState({
        open: true,
        phone: phoneRaw || "",
        error: "No phone number available for this transaction.",
      });
      return;
    }

    let year = "";

    const baseDateValue = tx?.transactionDate || tx?.processedAt;
    if (baseDateValue) {
      const d = baseDateValue.toDate
        ? baseDateValue.toDate()
        : new Date(baseDateValue);
      if (!Number.isNaN(d.getTime())) {
        year = String(d.getFullYear());
      }
    }

    if (!year && startDate) {
      year = String(startDate).split("-")[0] || "";
    }

    if (!year) {
      setPhoneModalState({
        open: true,
        phone,
        error: "Could not determine year to query phone-specific transactions.",
      });
      return;
    }

    try {
      const allRecords = [];

      for (let m = 1; m <= 12; m += 1) {
        const month = String(m).padStart(2, "0");

        const colRef = collection(
          firestore,
          "b2c_transactions_by_phone",
          phone,
          year,
          month,
          "tx"
        );

        const q = query(
          colRef,
          orderBy("processedAt", "desc"),
          limit(MAX_PHONE_LOOKUP_LIMIT)
        );

        const snap = await getDocs(q);
        snap.forEach((docSnap) => {
          allRecords.push({
            id: docSnap.id,
            ...docSnap.data(),
            __path: docSnap.ref.path,
          });
        });
      }

      allRecords.sort((a, b) => getTransactionTime(b) - getTransactionTime(a));

      setPhoneModalState({
        open: true,
        phone,
        records: allRecords,
        error: allRecords.length
          ? null
          : "No phone-specific records were found for this year.",
      });
    } catch (err) {
      console.error("Failed to load phone-specific transactions", err);
      setPhoneModalState({
        open: true,
        phone,
        error: "Failed to load phone records.",
      });
    }
  };

  const visibleRows = useMemo(() => rows, [rows]);
  const completedTotalPages = useMemo(
    () =>
      completedPageSize > 0
        ? Math.max(1, Math.ceil(visibleRows.length / completedPageSize))
        : 1,
    [visibleRows.length, completedPageSize]
  );
  const displayedCompletedRows = useMemo(() => {
    const startIndex = (completedPage - 1) * completedPageSize;
    const endIndex = startIndex + completedPageSize;
    return visibleRows.slice(startIndex, endIndex);
  }, [visibleRows, completedPageSize, completedPage]);

  const completedLookup = useMemo(() => {
    const map = new Map();
    visibleRows.forEach((row) => {
      if (String(row.status || "").toLowerCase() !== "completed") return;
      const key = getPaymentMatchKey(
        extractTxPhone(row),
        row.transactionAmount || row.amount
      );
      if (key && !map.has(key)) {
        map.set(key, row);
      }
    });
    return map;
  }, [visibleRows]);

  const proposedRowsWithMatches = useMemo(
    () =>
      proposedRows.map((payment) => {
        const enrichedPhone = getResolvedProposedPhone(
          payment,
          validatedFarmers
        );
        const key = getPaymentMatchKey(
          enrichedPhone,
          payment.Amount || payment.amount
        );
        return {
          ...payment,
          resolvedPhone: enrichedPhone,
          matchedTransaction: completedLookup.get(key) || null,
        };
      }),
    [proposedRows, completedLookup, validatedFarmers]
  );

  const {
    deduped: dedupedProposedRowsWithMatches,
    skippedCount,
    skippedByKey,
  } = useMemo(() => {
    const seen = new Set();
    const skippedByKeyInner = new Map();
    const result = [];
    let skipped = 0;

    for (const row of proposedRowsWithMatches) {
      const key = getPaymentMatchKey(
        row.resolvedPhone,
        row.Amount || row.amount
      );
      if (!key) {
        result.push(row);
        continue;
      }
      if (seen.has(key)) {
        skipped += 1;
        skippedByKeyInner.set(
          key,
          (skippedByKeyInner.get(key) || 0) + 1
        );
        continue;
      }
      seen.add(key);
      result.push(row);
    }
    return { deduped: result, skippedCount: skipped, skippedByKey: skippedByKeyInner };
  }, [proposedRowsWithMatches]);

  const proposedTotalPages = useMemo(
    () =>
      proposedPageSize > 0
        ? Math.max(
            1,
            Math.ceil(dedupedProposedRowsWithMatches.length / proposedPageSize)
          )
        : 1,
    [dedupedProposedRowsWithMatches.length, proposedPageSize]
  );

  const displayedProposedRows = useMemo(() => {
    const startIndex = (proposedPage - 1) * proposedPageSize;
    const endIndex = startIndex + proposedPageSize;
    return dedupedProposedRowsWithMatches.slice(startIndex, endIndex);
  }, [dedupedProposedRowsWithMatches, proposedPageSize, proposedPage]);

  const completedProposedRows = useMemo(
    () => dedupedProposedRowsWithMatches.filter((row) => row.matchedTransaction),
    [dedupedProposedRowsWithMatches]
  );
  const totalCompleted = visibleRows.length;
  const totalProposed = dedupedProposedRowsWithMatches.length;
  const matchedCount = completedProposedRows.length;

  const proposedMatchedKeys = useMemo(() => {
    const s = new Set();
    proposedRowsWithMatches.forEach((row) => {
      if (row.matchedTransaction) {
        const k = getPaymentMatchKey(
          row.resolvedPhone,
          row.Amount || row.amount
        );
        if (k) s.add(k);
      }
    });
    return s;
  }, [proposedRowsWithMatches]);

  const pendingRtdbRowsWithMatches = useMemo(() => {
    return pendingRtdbRows.map((payment) => {
      const phone =
        payment.PhoneNumber ||
        payment.phoneNumber ||
        payment.MSISDN ||
        payment.phone ||
        "";
      const key = getPaymentMatchKey(phone, payment.Amount || payment.amount);
      const matchedTransaction = completedLookup.get(key) || null;
      const matchedInProposed = !!key && proposedMatchedKeys.has(key);
      return {
        ...payment,
        resolvedPhone: phone,
        matchedTransaction,
        matchedInProposed,
        isMatched: !!matchedTransaction || matchedInProposed,
      };
    });
  }, [pendingRtdbRows, completedLookup, proposedMatchedKeys]);

  const pendingMatchedCount = useMemo(
    () => pendingRtdbRowsWithMatches.filter((r) => r.isMatched).length,
    [pendingRtdbRowsWithMatches]
  );

  const pendingTotalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(pendingRtdbRowsWithMatches.length / PENDING_PAGE_SIZE)
      ),
    [pendingRtdbRowsWithMatches.length]
  );

  const displayedPendingRows = useMemo(() => {
    const start = (pendingPage - 1) * PENDING_PAGE_SIZE;
    return pendingRtdbRowsWithMatches.slice(start, start + PENDING_PAGE_SIZE);
  }, [pendingRtdbRowsWithMatches, pendingPage]);

  const selectedCompany = useMemo(
    () =>
      companies.find((company) => company.id === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  const openReceiptModal = () => {
    if (!dedupedProposedRowsWithMatches.length) return;
    setReceiptModalState({
      open: true,
      error: "",
    });
  };

  const parseNumberOrZero = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };

  const handleConfirmGenerateReceipt = async () => {
    if (!dedupedProposedRowsWithMatches.length) return;
    setReceiptModalState({ submitting: true, error: "" });

    try {
      const purchaseAmount = parseNumberOrZero(receiptModal.purchaseAmount);
      const totalAmount = parseNumberOrZero(receiptModal.totalAmount);
      const transactionCost = parseNumberOrZero(receiptModal.transactionCost);
      const balance = parseNumberOrZero(receiptModal.balance);
      const advance = parseNumberOrZero(receiptModal.advance);

      const companyName = selectedCompany?.companyName || selectedCompanyId;
      const safeCompanyName = (companyName || "company").replace(/[^\w-]/g, "_");

      const sortedRows = [...dedupedProposedRowsWithMatches].sort((a, b) => {
        const nameA = getProposedDisplayName(a).toLowerCase();
        const nameB = getProposedDisplayName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const doc = new jsPDF("p", "pt", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();

      if (LOGO_DATA_URL) {
        try {
          const logoWidth = 80;
          const logoHeight = 40;
          doc.addImage(
            LOGO_DATA_URL,
            "PNG",
            pageWidth - logoWidth - 40,
            15,
            logoWidth,
            logoHeight
          );
        } catch (e) {
          console.warn("Failed to add logo", e);
        }
      }

      const pageHeight = doc.internal.pageSize.getHeight();
      const receiptTrackingId = `RCT-${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "")}-${Math.random()
        .toString(36)
        .slice(2, 10)
        .toUpperCase()}`;

      const drawWatermark = () => {
        doc.saveGraphicsState();
        doc.setTextColor(...RECEIPT_COLORS.watermark);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(60);
        doc.text("NEURACRAFT VERIFIED", pageWidth / 2, pageHeight / 2, {
          angle: 45,
          align: "center",
        });
        doc.restoreGraphicsState();
      };

      const drawFooter = () => {
        const footerY = pageHeight - 25;
        doc.setDrawColor(...RECEIPT_COLORS.primary);
        doc.setLineWidth(0.8);
        doc.line(40, footerY - 12, pageWidth - 40, footerY - 12);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...RECEIPT_COLORS.headerGrey);
        doc.text(
          "Thank you for your business! This is a computer-generated receipt from Neuracraft.",
          40,
          footerY
        );
        doc.text(`Tracking Id: ${receiptTrackingId}`, pageWidth - 40, footerY, {
          align: "right",
        });
      };

      // Watermark is drawn before page content so the table sits on top of it
      drawWatermark();

      doc.setFillColor(...RECEIPT_COLORS.primary);
      doc.rect(0, 0, pageWidth, 70, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Neuracraft Payment Receipt", 40, 35);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Company: ${companyName}`, 40, 50);
      doc.text(
        `Date range: ${proposedStartDate} to ${proposedEndDate}`,
        40,
        63
      );


      let y = 90;
      doc.setFillColor(...RECEIPT_COLORS.light);
      doc.roundedRect(30, y - 20, pageWidth - 60, 70, 6, 6, "F");
      doc.setDrawColor(...RECEIPT_COLORS.primary);
      doc.roundedRect(30, y - 20, pageWidth - 60, 70, 6, 6);

      doc.setTextColor(...RECEIPT_COLORS.headerGrey);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      const currency = "KES";

      const summaryLines = [
        `Purchase amount: ${currency} = ${purchaseAmount.toLocaleString()}`,
        `Total amount: ${currency} =  ${totalAmount.toLocaleString()}`,
        `Transaction cost: ${currency} = ${transactionCost.toLocaleString()}`,
        `Balance: ${currency} = ${balance.toLocaleString()}`,
        `Advance: ${currency} = ${advance.toLocaleString()}`,
      ];

      summaryLines.forEach((line, index) => {
        doc.text(line, 45, y + index * 12);
      });

      const tableStartY = y + 70;
      autoTable(doc, {
        startY: tableStartY,
        head: [["Name", "Phone", "Amount (KES)", "Receipt ID"]],
        body: sortedRows.map((row) => {
          const tx = row.matchedTransaction || {};
          const receiptId = tx.transactionReceipt || tx.Receipt || "No ID";
          return [
            getProposedDisplayName(row),
            row.resolvedPhone || "",
            Number(row.Amount || row.amount || 0).toLocaleString(),
            receiptId,
          ];
        }),
        styles: {
          fontSize: 9,
          cellPadding: 6,
          lineColor: RECEIPT_COLORS.border,
          lineWidth: 0.4,
        },
        headStyles: {
          fillColor: RECEIPT_COLORS.primary,
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: { fillColor: RECEIPT_COLORS.light },
        bodyStyles: { textColor: RECEIPT_COLORS.headerGrey },
        theme: "grid",
        margin: { bottom: 60 },
        willDrawPage: (data) => {
          if (data.pageNumber > 1) drawWatermark();
        },
        didDrawPage: drawFooter,
      });

      const finalY = doc.lastAutoTable?.finalY || tableStartY;
      doc.setFontSize(9);
      doc.setTextColor(...RECEIPT_COLORS.headerGrey);

      doc.text(
        `Generated on ${new Date().toLocaleString()} • Contact: +254768949692 • info@neuracraft.co.ke`,
        40,
        finalY + 18
      );

      const pdfFileName = `b2c_receipt_${safeCompanyName}_${proposedStartDate}_${proposedEndDate}.pdf`;
      const pdfBlob = doc.output("blob");

      const storage = getStorage();
      const now = new Date();
      const year = now.getFullYear().toString();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const path = `receipts/${year}/${month}/${safeCompanyName}/${pdfFileName}`;
      const ref = storageRef(storage, path);

      await uploadBytes(ref, pdfBlob, {
        contentType: "application/pdf",
      });

      doc.save(pdfFileName);

      setReceiptModalState({ submitting: false, open: false });
    } catch (err) {
      console.error("Failed to generate/upload PDF", err);
      setReceiptModalState({
        submitting: false,
        error: "Failed to generate or upload PDF. Please try again.",
      });
    }
  };

  const handleCompletedPageSizeChange = (value) => {
    setCompletedPageSize(value);
    setCompletedPage(1);
  };

  const handleProposedPageSizeChange = (value) => {
    setProposedPageSize(value);
    setProposedPage(1);
  };

  const handleClearCompleted = async () => {
    if (!database || !selectedCompanyId) return;
    setClearingPending(true);
    try {
      const matched = pendingRtdbRowsWithMatches.filter((row) => row.isMatched);
      await Promise.all(
        matched.map((row) =>
          rtdbRemove(
            rtdbRef(
              database,
              `Companies/${selectedCompanyId}/NewPaymentRecord/${row._key}`
            )
          )
        )
      );
      setPendingPage(1);
    } catch (err) {
      console.error("Failed to clear completed pending payments", err);
    } finally {
      setClearingPending(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100">
              <Wallet className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                B2C Payments
              </h1>
            </div>
          </div>
        </header>

        <section className="bg-white border border-slate-100 rounded-xl shadow-sm p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 items-center">
          <div className="col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <p className="text-xs text-emerald-700 uppercase tracking-wide">
                Completed
              </p>
              <p className="text-xl font-semibold text-emerald-900">
                {totalCompleted.toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-600 uppercase tracking-wide">
                Proposed
              </p>
              <p className="text-xl font-semibold text-slate-900">
                {totalProposed.toLocaleString()}
              </p>
            </div>
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
              <p className="text-xs text-indigo-700 uppercase tracking-wide">
                Matched Receipts
              </p>
              <p className="text-xl font-semibold text-indigo-900">
                {matchedCount.toLocaleString()}
              </p>
              <p className="text-xs text-indigo-700 mt-1">
                Pending Records: {pendingRtdbRowsWithMatches.length.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="col-span-2 flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <button
                onClick={handleClearCompleted}
                disabled={!pendingMatchedCount || clearingPending}
                className="px-3 py-2 text-sm font-semibold text-white bg-rose-600 rounded-lg hover:bg-rose-700 disabled:opacity-50"
              >
                {clearingPending ? "Clearing..." : "Clear Completed"}
              </button>
              <button
                onClick={openReceiptModal}
                disabled={!dedupedProposedRowsWithMatches.length}
                className="px-3 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                Generate Receipt PDF
              </button>
            </div>
            <div className="text-xs text-slate-500">
              {pendingMatchedCount} pending record{pendingMatchedCount !== 1 ? "s" : ""} matched
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
          {/* Completed table */}
          <section className="bg-white border border-slate-100 rounded-xl shadow-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-wrap gap-2">
              <div>
                <h2 className="text-base font-semibold text-slate-900">
                  Completed B2C Transactions
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) =>
                      handleDateChange(setStartDate)(e.target.value)
                    }
                    max={todayIso}
                    className="text-sm text-slate-700 outline-none"
                  />
                  <span className="text-xs text-slate-500">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) =>
                      handleDateChange(setEndDate)(e.target.value)
                    }
                    max={todayIso}
                    className="text-sm text-slate-700 outline-none"
                  />
                </div>
                <div className="text-xs text-slate-500">
                  {loadingRows ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    `Showing ${displayedCompletedRows.length} of ${visibleRows.length} record(s)`
                  )}
                </div>
              </div>
            </div>

            <div className="max-h-[460px] overflow-y-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Receiver
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loadingRows && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-4 text-sm text-slate-500 text-center"
                      >
                        <div className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading B2C transactions...
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loadingRows && visibleRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-4 text-sm text-slate-500 text-center"
                      >
                        No completed transactions found for the selected date
                        range.
                      </td>
                    </tr>
                  )}
                  {!loadingRows &&
                    displayedCompletedRows.map((row) => {
                      const phone = normalizePhone(extractTxPhone(row));
                      const name = row.receiverName || "Unknown";
                      return (
                        <tr key={row.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {formatDate(row.transactionDate || row.processedAt)}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            <div className="flex flex-col">
                              <span className="text-xs font-medium text-slate-800">
                                {phone || "—"}
                              </span>
                              <span className="text-[11px] text-slate-500 mt-0.5">
                                {name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {currencyLabel}{" "}
                            {Number(
                              row.transactionAmount || 0
                            ).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="px-4 py-3 space-x-2">
                            <button
                              className="px-3 py-1.5 text-xs font-semibold text-blue-700 border border-blue-200 rounded-lg inline-flex items-center gap-1 hover:bg-blue-50"
                              onClick={() => setSelected(row)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </button>
                            <button
                              className="px-3 py-1.5 text-xs font-semibold text-indigo-700 border border-indigo-200 rounded-lg inline-flex items-center gap-1 hover:bg-indigo-50"
                              onClick={() => openPhoneModalForTx(row)}
                            >
                              <Search className="h-3.5 w-3.5" />
                              Phone
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 gap-3 text-xs">
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                <span className="text-xs text-slate-500">Records</span>
                <select
                  value={completedPageSize}
                  onChange={(e) =>
                    handleCompletedPageSizeChange(Number(e.target.value))
                  }
                  className="text-sm text-slate-700 outline-none bg-transparent"
                >
                  {[10, 20, 50].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-2 py-1 border rounded disabled:opacity-40"
                  disabled={completedPage <= 1}
                  onClick={() =>
                    setCompletedPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  Prev
                </button>
                <span className="text-slate-600">
                  Page {completedPage} of {completedTotalPages}
                </span>
                <button
                  className="px-2 py-1 border rounded disabled:opacity-40"
                  disabled={completedPage >= completedTotalPages}
                  onClick={() =>
                    setCompletedPage((prev) =>
                      Math.min(completedTotalPages, prev + 1)
                    )
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </section>

          {/* Proposed table */}
          <section className="bg-white border border-slate-100 rounded-xl shadow-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-wrap gap-2">
              <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold text-slate-900">
                  Proposed company payments (RTDB)
                </h2>
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 w-fit">
                  <span className="text-xs text-slate-500">Company</span>
                  <select
                    value={selectedCompanyId}
                    onChange={(e) => {
                      setSelectedCompanyId(e.target.value);
                      setProposedPage(1);
                    }}
                    className="text-xs text-slate-700 outline-none bg-transparent"
                  >
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.companyName || company.id}
                      </option>
                    ))}
                  </select>
                  {loadingCompanies && (
                    <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                  )}
                </div>
                {skippedCount > 0 && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    Debug: Skipped {skippedCount} duplicate proposed record
                    {skippedCount > 1 ? "s" : ""} (same phone + amount).
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  <input
                    type="date"
                    value={proposedStartDate}
                    onChange={(e) =>
                      handleDateChange(setProposedStartDate)(e.target.value)
                    }
                    max={todayIso}
                    className="text-sm text-slate-700 outline-none"
                  />
                  <span className="text-xs text-slate-500">to</span>
                  <input
                    type="date"
                    value={proposedEndDate}
                    onChange={(e) =>
                      handleDateChange(setProposedEndDate)(e.target.value)
                    }
                    max={todayIso}
                    className="text-sm text-slate-700 outline-none"
                  />
                </div>
                <div className="text-xs text-slate-500">
                  {loadingProposedRows ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading...
                    </span>
                  ) : (
                    `Showing ${displayedProposedRows.length} of ${dedupedProposedRowsWithMatches.length} record(s)`
                  )}
                </div>
              </div>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Farmer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                      Completed
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loadingProposedRows && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-4 text-sm text-slate-500 text-center"
                      >
                        <div className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading proposed payments...
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loadingProposedRows &&
                    dedupedProposedRowsWithMatches.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-4 text-sm text-slate-500 text-center"
                        >
                          No proposed payments found for this company and date
                          range.
                        </td>
                      </tr>
                    )}
                  {!loadingProposedRows &&
                    displayedProposedRows.map((payment) => {
                      const key = getPaymentMatchKey(
                        payment.resolvedPhone,
                        payment.Amount || payment.amount
                      );
                      const duplicateCount = key
                        ? skippedByKey.get(key) || 0
                        : 0;
                      const isMatched = !!payment.matchedTransaction;
                      return (
                        <tr
                          key={payment._key}
                          className={
                            isMatched
                              ? "hover:bg-emerald-50 bg-emerald-50/40"
                              : duplicateCount > 0
                              ? "hover:bg-red-100 bg-red-50"
                              : "hover:bg-slate-50"
                          }
                        >
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {payment.TransactionDate
                              ? new Date(
                                  payment.TransactionDate
                                ).toLocaleString()
                              : payment.__date || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {getProposedDisplayName(payment)}
                            {duplicateCount > 0 && (
                              <span className="ml-1 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 border border-red-200">
                                Duplicate
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {payment.resolvedPhone || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {currencyLabel}{" "}
                            {Number(
                              payment.Amount || payment.amount || 0
                            ).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              status={
                                payment.matchedTransaction
                                  ? "completed"
                                  : "pending"
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 gap-3 text-xs">
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                <span className="text-xs text-slate-500">Records</span>
                <select
                  value={proposedPageSize}
                  onChange={(e) =>
                    handleProposedPageSizeChange(Number(e.target.value))
                  }
                  className="text-sm text-slate-700 outline-none bg-transparent"
                >
                  {[10, 20, 50].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-2 py-1 border rounded disabled:opacity-40"
                  disabled={proposedPage <= 1}
                  onClick={() =>
                    setProposedPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  Prev
                </button>
                <span className="text-slate-600">
                  Page {proposedPage} of {proposedTotalPages}
                </span>
                <button
                  className="px-2 py-1 border rounded disabled:opacity-40"
                  disabled={proposedPage >= proposedTotalPages}
                  onClick={() =>
                    setProposedPage((prev) =>
                      Math.min(proposedTotalPages, prev + 1)
                    )
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* Pending table */}
        <section className="bg-white border border-slate-100 rounded-xl shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-wrap gap-2">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Pending Payments
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Pending payment records from NewPaymentRecord — matched records can be cleared with the button above.
              </p>
            </div>
            <div className="text-xs text-slate-500">
              Showing {displayedPendingRows.length} of {pendingRtdbRowsWithMatches.length} record(s)
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pendingRtdbLoading && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-4 text-sm text-slate-500 text-center"
                    >
                      <div className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading pending payments...
                      </div>
                    </td>
                  </tr>
                )}
                {!pendingRtdbLoading && pendingRtdbRowsWithMatches.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-4 text-sm text-slate-500 text-center"
                    >
                      No pending payment records found for this company.
                    </td>
                  </tr>
                )}
                {!pendingRtdbLoading && displayedPendingRows.map((payment) => {
                  const isMatched = payment.isMatched;
                  return (
                    <tr
                      key={payment._key}
                      className={
                        isMatched
                          ? "hover:bg-slate-50 bg-emerald-50/40"
                          : "hover:bg-slate-50"
                      }
                    >
                      <td className="px-4 py-3 text-sm text-slate-800">
                        {payment.CloudName || payment.OtherName || "Unknown"}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">
                        {payment.resolvedPhone || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">
                        {currencyLabel}{" "}
                        {Number(
                          payment.Amount || payment.amount || 0
                        ).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800">
                        {payment.TransactionDate
                          ? new Date(payment.TransactionDate).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={isMatched ? "completed" : "pending"}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 gap-3 text-xs">
            <div className="text-xs text-slate-500">
              {PENDING_PAGE_SIZE} records per page
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-2 py-1 border rounded disabled:opacity-40"
                disabled={pendingPage <= 1}
                onClick={() => setPendingPage((prev) => Math.max(1, prev - 1))}
              >
                Prev
              </button>
              <span className="text-slate-600">
                Page {pendingPage} of {pendingTotalPages}
              </span>
              <button
                className="px-2 py-1 border rounded disabled:opacity-40"
                disabled={pendingPage >= pendingTotalPages}
                onClick={() =>
                  setPendingPage((prev) =>
                    Math.min(pendingTotalPages, prev + 1)
                  )
                }
              >
                Next
              </button>
            </div>
          </div>
        </section>

        {selected && (
          <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl border border-slate-100 p-6 relative">
              <button
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-800 text-lg"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                ×
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center border border-blue-100">
                  <Eye className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Transaction details
                  </h2>
                  <p className="text-sm text-slate-600">
                    Full callback payload for auditing
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <Detail
                  label="Receiver"
                  value={selected.receiverName || "Unknown"}
                />
                <Detail
                  label="Amount"
                  value={`${currencyLabel} ${Number(
                    selected.transactionAmount || 0
                  ).toLocaleString()}`}
                />
                <Detail
                  label="Status"
                  value={<StatusBadge status={selected.status} />}
                />
                <Detail
                  label="Transaction ID"
                  value={selected.transactionID || "—"}
                />
                <Detail
                  label="Receipt"
                  value={selected.transactionReceipt || "—"}
                />
                <Detail
                  label="Result Code"
                  value={selected.resultCode ?? "—"}
                />
                <Detail
                  label="Result Description"
                  value={selected.resultDesc || "—"}
                />
                <Detail
                  label="Conversation ID"
                  value={selected.conversationID || "—"}
                />
                <Detail
                  label="Originator Conversation ID"
                  value={selected.originatorConversationID || "—"}
                />
                <Detail
                  label="Date"
                  value={formatDate(
                    selected.transactionDate || selected.processedAt
                  )}
                />
                <Detail
                  label="Registered Recipient"
                  value={
                    selected.isRegistered === null ||
                    selected.isRegistered === undefined
                      ? "—"
                      : selected.isRegistered
                      ? "Yes"
                      : "No"
                  }
                />
                <Detail
                  label="Utility Balance"
                  value={selected.b2cUtilityBalance ?? "—"}
                />
                <Detail
                  label="Working Balance"
                  value={selected.b2cWorkingBalance ?? "—"}
                />
                <Detail
                  label="Charges Balance"
                  value={selected.chargesBalance ?? "—"}
                />
              </div>
              {selected.processedAt && (
                <p className="mt-4 text-xs text-slate-500">
                  Saved at: {formatDate(selected.processedAt)} �� ID: {selected.id}
                </p>
              )}
            </div>
          </div>
        )}

        {phoneModal.open && (
          <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl border border-slate-100 p-6 relative">
              <button
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-800 text-lg"
                onClick={closePhoneModal}
                aria-label="Close phone modal"
              >
                ×
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center border border-indigo-100">
                  <Search className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Phone-specific records
                  </h2>
                  <p className="text-sm text-slate-600">
                    Source: transactions • {phoneModal.phone || "Unknown"}
                  </p>
                </div>
              </div>
              {phoneModal.loading && (
                <div className="py-6 text-center text-sm text-slate-500">
                  <div className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading phone records...
                  </div>
                </div>
              )}
              {!phoneModal.loading && phoneModal.error && (
                <p className="py-4 text-sm text-red-600">{phoneModal.error}</p>
              )}
              {!phoneModal.loading &&
                !phoneModal.error &&
                phoneModal.records.length === 0 && (
                  <p className="py-4 text-sm text-slate-600">
                    No records found for this phone number.
                  </p>
                )}
              {!phoneModal.loading && phoneModal.records.length > 0 && (
                <div className="max-h-[60vh] overflow-auto border border-slate-100 rounded-lg">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                          Receiver
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {phoneModal.records.map((rec) => (
                        <tr key={rec.id}>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {formatDate(
                              rec.transactionDate || rec.processedAt
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {rec.receiverName || "Unknown"}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-800">
                            {currencyLabel}{" "}
                            {Number(
                              rec.transactionAmount || 0
                            ).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={rec.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {receiptModal.open && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md border border-slate-100 p-6 relative">
              <button
                className="absolute top-3 right-3 text-slate-500 hover:text-slate-800"
                onClick={resetReceiptModal}
                aria-label="Close receipt modal"
              >
                <X className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold text-slate-900 mb-1">
                Receipt summary details
              </h2>
              <p className="text-xs text-slate-500 mb-4">
                These values will appear at the top of the generated PDF.
              </p>
              <div className="space-y-3 text-sm">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Purchase amount ({currencyLabel})
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    value={receiptModal.purchaseAmount}
                    onChange={(e) =>
                      setReceiptModalState({
                        purchaseAmount: e.target.value,
                        error: "",
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Total amount ({currencyLabel})
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    value={receiptModal.totalAmount}
                    onChange={(e) =>
                      setReceiptModalState({
                        totalAmount: e.target.value,
                        error: "",
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Transaction cost ({currencyLabel})
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={receiptModal.transactionCost}
                      onChange={(e) =>
                        setReceiptModalState({
                          transactionCost: e.target.value,
                          error: "",
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Advance ({currencyLabel})
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={receiptModal.advance}
                      onChange={(e) =>
                        setReceiptModalState({
                          advance: e.target.value,
                          error: "",
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Balance ({currencyLabel})
                  </label>
                  <input
                    type="number"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    value={receiptModal.balance}
                    onChange={(e) =>
                      setReceiptModalState({
                        balance: e.target.value,
                        error: "",
                      })
                    }
                  />
                </div>
                {receiptModal.error && (
                  <p className="text-xs text-red-600">{receiptModal.error}</p>
                )}
              </div>
              <div className="mt-5 flex items-center justify-end gap-2 text-sm">
                <button
                  className="px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                  onClick={resetReceiptModal}
                  disabled={receiptModal.submitting}
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
                  onClick={handleConfirmGenerateReceipt}
                  disabled={receiptModal.submitting}
                >
                  {receiptModal.submitting && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Generate PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const Detail = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
    <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">
      {label}
    </p>
    <div className="text-slate-800 font-medium break-words">{value}</div>
  </div>
);

export default AdminB2CTransactions;
